import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type ReportRow } from '../api';

type FailureFilter = 'any' | 'dmarc' | 'spf' | 'dkim';

export function ReportListPage() {
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [domain, setDomain] = useState('');
  const [domains, setDomains] = useState<string[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [failingOnly, setFailingOnly] = useState(false);
  const [failureFilter, setFailureFilter] = useState<FailureFilter>('any');

  useEffect(() => {
    api.reportDomains().then((d) => setDomains(d.domains.map((x) => x.domain)));
  }, []);

  function load() {
    setLoading(true);
    api
      .reportList({
        domain: domain || undefined,
        from: from ? `${from}T00:00:00Z` : undefined,
        to: to ? `${to}T23:59:59Z` : undefined,
        failingOnly,
      })
      .then((d) => setRows(d.reports))
      .finally(() => setLoading(false));
  }
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (failureFilter === 'any') return rows;
    return rows.filter((r) => {
      if (failureFilter === 'dmarc') return Number(r.fail_volume ?? 0) > 0;
      if (failureFilter === 'spf') return Number(r.spf_fail_volume ?? 0) > 0;
      if (failureFilter === 'dkim') return Number(r.dkim_fail_volume ?? 0) > 0;
      return true;
    });
  }, [rows, failureFilter]);

  function reset() {
    setDomain('');
    setFrom('');
    setTo('');
    setFailingOnly(false);
    setFailureFilter('any');
    setTimeout(load, 0);
  }

  return (
    <>
      <div className="card">
        <h2>Imported reports</h2>
        <div className="flex" style={{ flexWrap: 'wrap', gap: 8 }}>
          <select value={domain} onChange={(e) => setDomain(e.target.value)} style={{ flex: '0 0 220px' }}>
            <option value="">All domains</option>
            {domains.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <label className="muted" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            From
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ width: 160 }} />
          </label>
          <label className="muted" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            To
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ width: 160 }} />
          </label>
          <label className="muted" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="checkbox"
              checked={failingOnly}
              onChange={(e) => setFailingOnly(e.target.checked)}
            />
            Only reports with DMARC failures
          </label>
          <select
            value={failureFilter}
            onChange={(e) => setFailureFilter(e.target.value as FailureFilter)}
            style={{ flex: '0 0 220px' }}
            title="Client-side filter on what the row contains"
          >
            <option value="any">Show all</option>
            <option value="dmarc">Has DMARC failures</option>
            <option value="spf">Has any SPF fails</option>
            <option value="dkim">Has any DKIM fails</option>
          </select>
          <button onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Apply'}</button>
          <button className="secondary" onClick={reset} disabled={loading}>Reset</button>
        </div>
      </div>

      <div className="card">
        {filtered.length === 0 ? (
          <p className="muted">No reports match.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Domain</th>
                <th>Provider</th>
                <th>Window</th>
                <th>Policy</th>
                <th>Volume</th>
                <th>DMARC fail</th>
                <th>SPF fail</th>
                <th>DKIM fail</th>
                <th>Imported</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const vol = Number(r.volume ?? 0);
                const failDmarc = Number(r.fail_volume ?? 0);
                const failSpf = Number(r.spf_fail_volume ?? 0);
                const failDkim = Number(r.dkim_fail_volume ?? 0);
                const passRate = vol > 0 ? 1 - failDmarc / vol : 1;
                return (
                  <tr key={r.id}>
                    <td>
                      <Link to={`/reports/${r.id}`}>{r.domain}</Link>
                    </td>
                    <td>{r.org_name || '—'}</td>
                    <td className="muted">
                      {new Date(r.date_begin).toISOString().slice(0, 10)} →{' '}
                      {new Date(r.date_end).toISOString().slice(0, 10)}
                    </td>
                    <td>
                      p={r.policy_p ?? '—'} pct={r.policy_pct ?? 100}
                    </td>
                    <td>{vol.toLocaleString()}</td>
                    <td style={{ color: failDmarc > 0 ? 'var(--fail)' : 'var(--muted)' }}>
                      {failDmarc.toLocaleString()}{vol > 0 && failDmarc > 0 && ` (${(100 - passRate * 100).toFixed(1)}%)`}
                    </td>
                    <td style={{ color: failSpf > 0 ? 'var(--warn)' : 'var(--muted)' }}>
                      {failSpf.toLocaleString()}
                    </td>
                    <td style={{ color: failDkim > 0 ? 'var(--warn)' : 'var(--muted)' }}>
                      {failDkim.toLocaleString()}
                    </td>
                    <td className="muted">
                      {r.imported_at ? new Date(r.imported_at).toLocaleString() : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
